// Enemy waves and fully automatic combat (era-gated). On a timer a raider wave
// attacks: the settlement's single defense number is compared against the wave's
// single threat number. Threat is driven by the current AGE, ramping gently
// within an age. When the settlement crosses its age's progress threshold the
// era BOSS is summoned to the gate (systems/ages.ts sets run.gatePending): the
// next wave to telegraph is that boss, it gathers for longer, and only defeating
// it opens the card choice that advances the age. Losing any wave ends the run.

import type { GameState } from '@core/state';
import { BALANCE } from '@data/balance';
import { waveSpoils } from '@data/enemies';
import { RESOURCE_ORDER } from '@data/resources';
import { settlementDefense, effectiveThreat, computeIncome } from './stats';
import { killRun } from './prestige';
import { openGateReward } from './ages';
import { dropArtifact } from './artifacts';
import { bus } from '@core/events';

function resolveWave(state: GameState): void {
  const run = state.run;
  const boss = run.combatIsBoss;
  const threat = effectiveThreat(state, boss);
  const defense = settlementDefense(state);
  const win = defense >= threat;
  const wave = run.wave + 1;

  if (!win) {
    bus.emit('wave:result', { wave, win: false, defense, threat, rewardSpoils: 0, isBoss: boss });
    killRun(state, wave);
    return;
  }

  // Held the line: count the cleared wave.
  run.wave = wave;
  state.meta.bestWave = Math.max(state.meta.bestWave, wave);

  const spoils = waveSpoils(run.age, boss);
  run.spoils += spoils;

  // Resource reward: roughly ten seconds of current income, as a bonus.
  // Deliberately progress-exempt: only active production/harvest feed the age
  // gate, so a combat windfall never summons the gate boss on its own.
  const income = computeIncome(state);
  for (const id of RESOURCE_ORDER) {
    const r = income[id] * BALANCE.waves.rewardResourceFactor * 10;
    if (r > 0) run.resources[id] += r;
  }

  run.combatResolveAt = 0;
  run.combatIsBoss = false;
  run.nextWaveAt = state.gameTime + BALANCE.waves.interval * run.modifiers.waveIntervalMult;

  bus.emit('wave:result', { wave, win: true, defense, threat, rewardSpoils: spoils, isBoss: boss });

  if (boss) {
    // The era gate boss fell: drop its artifact and open the age-advance card
    // choice. openGateReward pauses the sim; picking a card clears gatePending,
    // resets the within-age ramp, and advances the age.
    dropArtifact(state);
    openGateReward(state);
  } else {
    // A raider was repelled: the within-age ramp climbs, so camping an age
    // (not clearing its gate boss) slowly grinds the settlement down.
    run.wavesThisAge += 1;
  }
}

/** Player-initiated skip: stop waiting and pull the next wave in NOW. The
 *  telegraph still plays, so the battle keeps its drama; only the idle wait is
 *  skipped. Works for the era gate boss too (pull the gate fight in early).
 *  No-op while a wave is already marching. */
export function callWaveNow(state: GameState): boolean {
  const run = state.run;
  if (run.combatResolveAt > 0) return false; // already telegraphed
  if (state.gameTime >= run.nextWaveAt) return false; // firing this tick anyway
  run.nextWaveAt = state.gameTime;
  return true;
}

export function updateWaves(state: GameState): void {
  const run = state.run;

  // Peace -> telegraph. Latch whether THIS wave is the era gate boss so the
  // telegraph lead, banner, and resolution all agree. The gate boss gathers for
  // longer (a distinct, dread-building event) than a raider.
  if (run.combatResolveAt === 0 && state.gameTime >= run.nextWaveAt) {
    const boss = run.gatePending;
    run.combatIsBoss = boss;
    const lead = boss ? BALANCE.waves.gateTelegraph : BALANCE.waves.warnLead;
    run.combatResolveAt = state.gameTime + lead;
    bus.emit('wave:incoming', { wave: run.wave + 1, isBoss: boss });
    return;
  }

  // Telegraph -> resolve.
  if (run.combatResolveAt > 0 && state.gameTime >= run.combatResolveAt) {
    resolveWave(state);
  }
}
